// Shared bin-location sort helpers for Warehouse-style aisle codes.
//
// Aisle order is bijective base-26 (Excel-column style):
//   A, B, C, ... , Z, AA, AB, ... , ZZ, AAA, ...
// Within an aisle, items sort by numeric position. Empty/null bins sort last.
//
// Used by:
//   - PickingPortal.jsx (Warehouse location sort)
//   - OrderPrintCard.jsx (Warehouse location print order)
//   - orders-v2/OrdersTanStack.jsx (bulk pick-list aisle grouping)

const SENTINEL_END = '￿';

// Extract the aisle letters from a bin code: "AD-05" -> "AD", "J-09" -> "J",
// "Z" -> "Z". Returns "" if the leading characters aren't letters.
export function binAisle(bin) {
    const s = String(bin || '').trim();
    if (!s) return '';
    const m = s.match(/^([A-Za-z]+)/);
    return m ? m[1].toUpperCase() : '';
}

// Returns a comparable array for sorting. Compose:
//   [aisle_length, aisle_letters, position_number, tail_string]
// Empty/null bin sorts last via SENTINEL_END.
export function binSortKey(bin) {
    const s = String(bin || '').trim();
    if (!s) return [Infinity, SENTINEL_END, Infinity, SENTINEL_END];
    const m = s.match(/^([A-Za-z]+)\s*[-_/ ]?\s*(\d+)?(.*)$/);
    if (!m) return [Infinity, s.toUpperCase(), 0, s.toUpperCase()];
    const aisle = m[1].toUpperCase();
    const pos   = parseInt(m[2] || '0', 10) || 0;
    const tail  = (m[3] || '').toUpperCase().trim();
    return [aisle.length, aisle, pos, tail];
}

// Comparator (compatible with Array.sort) for two bin strings.
export function compareBins(a, b) {
    const ka = binSortKey(a), kb = binSortKey(b);
    for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
        const va = ka[i], vb = kb[i];
        if (typeof va === 'number' && typeof vb === 'number') {
            if (va !== vb) return va - vb;
        } else {
            const cmp = String(va == null ? '' : va).localeCompare(String(vb == null ? '' : vb));
            if (cmp !== 0) return cmp;
        }
    }
    return 0;
}

// "Is this location a Warehouse?" — the user's rule is name-regex /warehouse/i.
// Drives whether to apply binSort to printout / picking-portal output.
export function isWarehouseLocation(locationName) {
    return /warehouse/i.test(locationName || '');
}
